// ?show-feedback=true

'use client';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PurpleGradientButton } from '@/components/ui/purple-gradient-button';
import { StarRating } from '@/components/ui/star-rating';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { Link } from '@/i18n/routing';
import feedbackService from '@/services/feedback-services';
import { useTranslations } from 'next-intl';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';

import { useAuth } from '@/hooks/useAuth';
import { useAuthUser } from '@/hooks/useAuthUser';

export default function FeedbackModal() {
  const { isAuthenticated: isSignedIn, isLoading } = useAuth();
  const { userEmail } = useAuthUser();
  const isLoaded = !isLoading;
  const [rating, setRating] = useState<number | null>(2.5);
  const [whatIsLoved, setWhatIsLoved] = useState('');
  const [whatToImprove, setWhatToImprove] = useState('');
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();
  const t = useTranslations('FeedbackPage');
  const { toast } = useToast();

  useEffect(() => {
    if (isLoaded && isSignedIn) {
      setEmail(userEmail ?? '');
    }
  }, [isLoaded, isSignedIn, userEmail]);

  const isOpen = searchParams.get('show-feedback') === 'true';

  const handleCloseAndReset = () => {
    setRating(2.5);
    setWhatIsLoved('');
    setWhatToImprove('');
    setEmail('');
    setIsSubmitting(false);

    const current = new URLSearchParams(Array.from(searchParams.entries()));
    current.delete('show-feedback');
    const search = current.toString();
    const query = search ? `?${search}` : '';
    router.replace(`${pathname}${query}`, { scroll: false });
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      handleCloseAndReset();
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await feedbackService.createFeedback({
        rating: rating ?? 2.5,
        whatIsLoved,
        whatToImprove,
        email,
      });

      toast({
        title: t('thankYou'),
        description: t('feedbackSubmitted'),
        variant: 'success',
      });

      handleCloseAndReset();
    } catch (error) {
      console.error('Feedback submission error:', {
        error: error instanceof Error ? error.message : String(error),
      });
      toast({
        title: t('errorSubmitting'),
        description: t('tryAgainLater'),
        variant: 'destructive',
      });
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className='sm:max-w-[600px] max-h-[90vh] overflow-y-auto no-scrollbar'>
        <DialogHeader>
          <DialogTitle className='text-center text-2xl font-bold mb-4'>
            {t('title')}
          </DialogTitle>
          <DialogDescription className='p-3 mb-4 text-sm rounded-lg bg-muted text-foreground/80'>
            {t('description')}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className='px-2 py-4 space-y-4'>
          <div className='text-center'>
            <Label className='block mb-1'>{t('rateExperience')}</Label>
            <StarRating
              value={rating}
              precision={0.5}
              onChange={setRating}
              ariaLabel={t('rateExperience')}
            />
          </div>

          <div>
            <Label htmlFor='what_is_loved' className='block mb-2 font-medium'>
              {t('whatLoved')}
            </Label>
            <Textarea
              id='what_is_loved'
              name='what_is_loved'
              value={whatIsLoved}
              onChange={(e) => setWhatIsLoved(e.target.value)}
              placeholder={t('whatLoved')}
              className='min-h-[100px] resize-y dark:bg-surface-subtle'
            />
          </div>

          <div>
            <Label htmlFor='what_to_improve' className='block mb-2 font-medium'>
              {t('whatImprove')}
            </Label>
            <Textarea
              id='what_to_improve'
              name='what_to_improve'
              value={whatToImprove}
              onChange={(e) => setWhatToImprove(e.target.value)}
              placeholder={t('whatImprove')}
              className='min-h-[100px] resize-y dark:bg-surface-subtle'
            />
          </div>

          <div>
            <Label htmlFor='email' className='block mb-2 font-medium'>
              {t('emailOptional')}
            </Label>
            <Input
              id='email'
              type='email'
              name='email'
              placeholder={t('enterEmail')}
              value={email}
              disabled={isLoaded && isSignedIn}
              onChange={(e) => setEmail(e.target.value)}
              className='w-full h-10 px-3 py-2 rounded-md outline-none border border-input bg-background dark:bg-surface-subtle text-foreground placeholder:text-muted-foreground
              focus-visible:ring-1 focus-visible:ring-ring'
            />
          </div>

          <p className='text-xs text-muted-foreground'>
            {t.rich('termsAgreement', {
              terms: (chunks) => (
                <Link
                  href='/terms-of-use'
                  className='text-primary font-medium underline underline-offset-4 hover:no-underline focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded-sm'
                >
                  {chunks}
                </Link>
              ),
              privacy: (chunks) => (
                <Link
                  href='/privacy-policy'
                  className='text-primary font-medium underline underline-offset-4 hover:no-underline focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded-sm'
                >
                  {chunks}
                </Link>
              ),
            })}
          </p>

          <PurpleGradientButton
            type='submit'
            disabled={isSubmitting}
            className='w-full h-11'
          >
            {isSubmitting ? t('submitting') : t('submitFeedback')}
          </PurpleGradientButton>
        </form>
      </DialogContent>
    </Dialog>
  );
}
